import { useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { Card, CardBody, Col, Row } from 'reactstrap'
import BreadCrumbs from 'components/breadcrumbs'
import LoadingAnimation from 'components/LoadingAnimation'

import PMBPagination from 'components/PMBPagination'
import { URL_DASHBOARD_TRACEABILITY } from 'utils/pathUrl'
import {
  useFetchInventoriesTraceabilityV2Query,
  useLazyFetchInventoriesTraceabilityV2Query,
  useFetchSummaryTraceabilityV2Query,
} from '../dashboardTraceabilityApiSlice'
import { P } from 'components'
import { newFormatThousandSeparator } from 'utils/formatNumber'
// removed archer
import { Download, ChevronDown, ChevronUp } from 'react-feather'
import CustomButton from 'components/Buttons/CustomButton'
import PMBProgressBarExportFile from 'components/PMBProgressBarExportFile'
import { handleDownloadExcel } from '../downloadDashboardTraceability'

// Helper Component Removed per V2 rules

const ProcessUserRow = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <div
      className="border rounded mb-2 overflow-hidden"
      style={{ fontSize: '12px' }}
    >
      <div
        className="d-flex justify-content-between align-items-center p-2"
        style={{ backgroundColor: '#f8fafc', cursor: 'pointer' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="d-flex align-items-center gap-2">
          {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          <span className="text-break">{user.userName || '-'}</span>
        </div>
        <div className="text-end">
          <div className="fw-bold">
            {newFormatThousandSeparator(user.total || 0)}
          </div>
          {/* {user.percentage !== undefined && user.percentage !== null && (
            <div
              className={`fw-semibold ${user.percentage > 0 ? 'text-danger' : 'text-success'}`}
              style={{ fontSize: '10px' }}
            >
              Loss {user.percentage}%
            </div>
          )} */}
        </div>
      </div>
      {isOpen && user.invResult && user.invResult.length > 0 && (
        <div className="p-2 bg-white border-top">
          {user.invResult.map((res, rIdx) => (
            <div
              key={rIdx}
              className="d-flex justify-content-between align-items-center py-1 border-bottom border-light"
            >
              <span>{res.item?.nameItem || res.itemName || '-'}</span>
              <span className="fw-bold">
                {newFormatThousandSeparator(
                  res.initialAmount ?? res.amount ?? 0,
                )}{' '}
                {res.item?.unit || res.unit || ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const monthsLabel = [
  { value: '1', label: 'Januari' },
  { value: '2', label: 'Februari' },
  { value: '3', label: 'Maret' },
  { value: '4', label: 'April' },
  { value: '5', label: 'Mei' },
  { value: '6', label: 'Juni' },
  { value: '7', label: 'Juli' },
  { value: '8', label: 'Agustus' },
  { value: '9', label: 'September' },
  { value: '10', label: 'Oktober' },
  { value: '11', label: 'November' },
  { value: '12', label: 'Desember' },
]

export default function DashboardTraceabilityDetail() {
  const { id } = useParams()
  const location = useLocation()
  const { configData } = location.state || {}

  const [currentPage, setCurrentPage] = useState(0)
  const [rowPerPage, setRowsPerPage] = useState(10)

  const [isDownload, setIsDownload] = useState(false)
  const [firstRows, setFirstRows] = useState(0)
  const [totalRows, setTotalRows] = useState(0)

  const {
    data: inventoriesData,
    isLoading: isLoadingInventories,
    isFetching: isFetchingInventories,
  } = useFetchInventoriesTraceabilityV2Query(
    {
      dashboardTraceabilityId: id,
      page: currentPage,
      size: rowPerPage,
      sortby: 'createdAt',
      sortdir: 'asc',
    },
    { skip: !id },
  )

  const [triggerFetchForDownload] = useLazyFetchInventoriesTraceabilityV2Query()

  const {
    data: summaryData,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
  } = useFetchSummaryTraceabilityV2Query(
    {
      dashboardTraceabilityId: id,
    },
    { skip: !id },
  )

  const inventories = inventoriesData?.items || []
  const totalItems = inventoriesData?.totalItems || 0

  const rawSummaryList =
    summaryData?.items || (Array.isArray(summaryData) ? summaryData : [])

  const summaryItems = rawSummaryList.reduce((acc, curr) => {
    if (curr?.inventories && Array.isArray(curr.inventories)) {
      return acc.concat(curr.inventories)
    }
    return acc.concat(curr)
  }, [])

  // Totals from the given inventories; export passes all of them, not just this page
  const computeTotals = (invs) => {
    const totalInput = invs.reduce(
      (acc, curr) => acc + Number(curr.initialAmount ?? curr.amount ?? 0),
      0,
    )

    const allProcessesMap = new Map()
    invs.forEach((inv) => {
      ;(inv.summaryProcessData || []).forEach((proc) => {
        if (proc.processschemaId && !allProcessesMap.has(proc.processschemaId)) {
          allProcessesMap.set(proc.processschemaId, proc)
        }
      })
    })

    const rawProcesses = Array.from(allProcessesMap.values())
    const processedProcesses = rawProcesses.map((proc, index) => {
      const totalProc = (proc.userData || []).reduce(
        (acc, u) => acc + Number(u.total ?? 0),
        0,
      )
      const prevTotal =
        index === 0
          ? totalInput
          : (rawProcesses[index - 1].userData || []).reduce(
              (acc, u) => acc + Number(u.total ?? 0),
              0,
            )
      const loss = prevTotal
        ? Math.round(((prevTotal - totalProc) / prevTotal) * 100 * 100) / 100
        : 0
      return { ...proc, totalProc, loss }
    })

    const lastProcessTotal =
      processedProcesses.length > 0
        ? processedProcesses[processedProcesses.length - 1].totalProc
        : totalInput
    const totalOutput = summaryItems.reduce(
      (acc, out) => acc + Number(out?.total ?? out?.amount ?? 0),
      0,
    )
    const outputLoss = lastProcessTotal
      ? Math.round(
          ((lastProcessTotal - totalOutput) / lastProcessTotal) * 100 * 100,
        ) / 100
      : 0
    return { totalInput, processedProcesses, totalOutput, outputLoss }
  }

  const isLoading =
    isLoadingInventories ||
    isFetchingInventories ||
    isLoadingSummary ||
    isFetchingSummary

  return (
    <div className="w-100 px-3">
      <PMBProgressBarExportFile
        isDialogShow={isDownload}
        firstRows={firstRows}
        totalRows={totalRows}
        onCancel={() => setIsDownload(false)}
      />
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs
          breadCrumbActive={id}
          breadCrumbParent="Dashboard Ketelusuran"
          breadCrumbParentLink={URL_DASHBOARD_TRACEABILITY}
        />
      </div>

      {configData && (
        <Card className="shadow-sm border-0 mb-3">
          <CardBody>
            <h6 className="fw-semibold mb-3">
              Informasi Dashboard Ketelusuran
            </h6>
            <Row className="mb-2">
              <Col xs="12" sm="2" className="fw-light text-sm">
                Judul
              </Col>
              <Col xs="12" sm="10" className="fw-semibold text-sm">
                {configData.title || '-'}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs="12" sm="2" className="fw-light text-sm">
                Periode
              </Col>
              <Col xs="12" sm="10" className="fw-semibold text-sm">
                {monthsLabel.find((m) => m.value === String(configData.month))
                  ?.label || configData.month}{' '}
                {configData.year}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs="12" sm="2" className="fw-light text-sm">
                Anggota Awal
              </Col>
              <Col xs="12" sm="10" className="fw-semibold text-sm">
                {configData.startUser?.name || '-'}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs="12" sm="2" className="fw-light text-sm">
                Anggota Terlacak
              </Col>
              <Col xs="12" sm="10" className="fw-semibold text-sm">
                {configData.trackedUsers &&
                configData.trackedUsers.length > 0 ? (
                  <ol className="mb-0 ps-3">
                    {configData.trackedUsers.map((u, i) => (
                      <li key={u.userId || i}>{u.name}</li>
                    ))}
                  </ol>
                ) : (
                  '-'
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      <div className="pb-3 d-flex justify-content-end">
        <CustomButton
          onClick={async () => {
            const all = await triggerFetchForDownload({
              dashboardTraceabilityId: id,
              page: 0,
              size: totalItems || rowPerPage,
              sortby: 'createdAt',
              sortdir: 'asc',
            }).unwrap()
            const allInventories = all?.items || []
            handleDownloadExcel(
              {
                inventories: allInventories,
                summaryItems,
                configData,
                ...computeTotals(allInventories),
              },
              setIsDownload,
              setFirstRows,
              setTotalRows,
              id,
            )
          }}
          outline
          color="primary"
          testId="trace-download-button"
        >
          <Download size={15} /> Download
        </CustomButton>
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center py-4">
          <LoadingAnimation />
        </div>
      )}

      {!isLoading && (
        <div style={{ padding: '16px', margin: '-16px' }}>
          {inventories.length === 0 ? (
            <Card className="shadow-sm border-0">
              <CardBody>
                <P>Tidak ada data.</P>
              </CardBody>
            </Card>
          ) : (
            inventories.map((inv, invIdx) => {
              // Filter Inputs (Barang Mentah) for this Output
              const inputItems = summaryItems.filter(
                (s) =>
                  inv.summaryInvIds?.includes(s.id) ||
                  inv.summaryInvIds?.includes(String(s.id)),
              )

              const inputTotal = inputItems.reduce(
                (acc, curr) =>
                  acc + Number(curr.initialAmount ?? curr.amount ?? 0),
                0,
              )

              const processes = inv.summaryProcessData || []

              // Process calculations for sequential loss
              const processedProcesses = processes.map((proc, index) => {
                const totalProc = (proc.userData || []).reduce(
                  (acc, u) => acc + Number(u.total ?? 0),
                  0,
                )
                const prevTotal =
                  index === 0
                    ? inputTotal
                    : (processes[index - 1].userData || []).reduce(
                        (acc, u) => acc + Number(u.total ?? 0),
                        0,
                      )
                const loss = prevTotal
                  ? Math.round(
                      ((prevTotal - totalProc) / prevTotal) * 100 * 100,
                    ) / 100
                  : 0
                return { ...proc, totalProc, loss }
              })

              const lastProcessTotal =
                processedProcesses.length > 0
                  ? processedProcesses[processedProcesses.length - 1].totalProc
                  : inputTotal

              const outputTotal = Number(inv.amount ?? inv.initialAmount ?? 0)
              const outputLoss = lastProcessTotal
                ? Math.round(
                    ((lastProcessTotal - outputTotal) / lastProcessTotal) *
                      100 *
                      100,
                  ) / 100
                : 0

              return (
                <div
                  key={inv.id || invIdx}
                  className="mb-4 shadow-sm"
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#fff',
                    overflowX: 'auto',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: '40px',
                      minWidth: 'max-content',
                    }}
                  >
                    {/* 1. KOTAK BARANG MENTAH & INFO BARANG JADI */}
                    <Card
                      className="border-0 shadow-none border h-100"
                      style={{ width: '320px', flexShrink: 0 }}
                    >
                      <CardBody className="p-3">
                        <div
                          className="d-flex justify-content-between align-items-center border-bottom mb-3 pb-3"
                          style={{ minHeight: '65px' }}
                        >
                          <h6 className="fw-bold mb-0">
                            {inv.item?.nameItem ??
                              inv.comodity?.name ??
                              inv.commodityName ??
                              '-'}
                          </h6>
                          {inv.item?.linkFoto && (
                            <img
                              src={inv.item.linkFoto}
                              alt="Gambar"
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 4,
                                objectFit: 'cover',
                                border: '1px solid #e2e8f0',
                              }}
                            />
                          )}
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {inputItems.length === 0 && (
                            <P size="small" className="text-muted">
                              Tidak ada data barang mentah.
                            </P>
                          )}
                          {inputItems.map((inItem, iIdx) => (
                            <div
                              key={iIdx}
                              className="d-flex justify-content-between align-items-center p-2 rounded"
                              style={{
                                backgroundColor: '#f8fafc',
                                fontSize: '12px',
                              }}
                            >
                              <div className="d-flex flex-column">
                                <span className="mb-1">
                                  {inItem.item?.nameItem ||
                                    inItem.itemName ||
                                    '-'}
                                </span>
                                <span
                                  className="text-muted"
                                  style={{ fontSize: '10px' }}
                                >
                                  {inItem.ownerpartnership?.name ||
                                    inItem.creator?.name ||
                                    inItem.owner?.name ||
                                    '-'}
                                </span>
                              </div>
                              <span className="fw-bold">
                                {newFormatThousandSeparator(
                                  inItem.initialAmount ?? inItem.amount ?? 0,
                                )}{' '}
                                {inItem.unit || ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardBody>
                    </Card>

                    {/* 2. KOTAK PROSES */}
                    {processedProcesses.map((proc, pIdx) => (
                      <div
                        key={pIdx}
                        className="d-flex align-items-stretch gap-4"
                      >
                        <div
                          className="d-flex align-items-center"
                          style={{ color: '#94a3b8' }}
                        >
                          ➔
                        </div>
                        <Card
                          className="border-0 shadow-none border h-100"
                          style={{ width: '280px', flexShrink: 0 }}
                        >
                          <CardBody className="p-3 d-flex flex-column h-100">
                            <div
                              className="d-flex flex-column justify-content-center align-items-center border-bottom mb-3 pb-3"
                              style={{ minHeight: '65px' }}
                            >
                              <h6 className="fw-bold mb-1 text-center">
                                {proc.processingName || `Proses ${pIdx + 1}`}
                              </h6>
                              {/* {proc.percentage !== undefined &&
                                proc.percentage !== null && (
                                  <span
                                    style={{ fontSize: '12px' }}
                                    className={`fw-bold text-center ${proc.percentage > 0 ? 'text-danger' : 'text-success'}`}
                                  >
                                    Loss {proc.percentage}%
                                  </span>
                                )} */}
                            </div>
                            <div className="flex-grow-1 d-flex flex-column mb-1">
                              {(proc.userData || []).map((user, uIdx) => (
                                <ProcessUserRow key={uIdx} user={user} />
                              ))}
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    ))}

                    {/* 3. KOTAK FINAL OUTPUT */}
                    <div className="d-flex align-items-stretch gap-4">
                      <div
                        className="d-flex align-items-center"
                        style={{ color: '#94a3b8' }}
                      >
                        ➔
                      </div>
                      <Card
                        className="border-0 shadow-none border h-100"
                        style={{ width: '280px', flexShrink: 0 }}
                      >
                        <CardBody className="p-3 d-flex flex-column h-100">
                          <div
                            className="d-flex flex-column justify-content-center align-items-center border-bottom mb-3 pb-3"
                            style={{ minHeight: '65px' }}
                          >
                            <h6 className="fw-bold mb-1 text-center">
                              Hasil Akhir
                            </h6>
                            {/* {inv.percentage !== undefined &&
                              inv.percentage !== null && (
                                <span
                                  style={{ fontSize: '12px' }}
                                  className={`fw-bold text-center ${inv.percentage > 0 ? 'text-danger' : 'text-success'}`}
                                >
                                  Loss {inv.percentage}%
                                </span>
                              )} */}
                          </div>
                          <div className="flex-grow-1 d-flex flex-column gap-2">
                            <div
                              className="d-flex justify-content-between p-2 rounded align-items-center"
                              style={{
                                backgroundColor: '#f8fafc',
                                fontSize: '12px',
                              }}
                            >
                              <div className="d-flex flex-column">
                                <span className="mb-1">
                                  {inv.item?.nameItem ??
                                    inv.comodity?.name ??
                                    inv.commodityName ??
                                    '-'}
                                </span>
                                {inv.lotData && inv.lotData.length > 0 ? (
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: '10px' }}
                                  >
                                    Lot:{' '}
                                    {inv.lotData
                                      .map((lot) => lot.lotCode || '-')
                                      .join(', ')}
                                  </span>
                                ) : inv.lotCode ? (
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: '10px' }}
                                  >
                                    Lot: {inv.lotCode}
                                  </span>
                                ) : null}
                              </div>
                              <span className="fw-bold text-primary">
                                {newFormatThousandSeparator(outputTotal)}{' '}
                                {inv.unit || ''}
                              </span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                </div>
              )
            })
          )}

          <div className="mt-2">
            <PMBPagination
              rowCount={totalItems}
              currentPage={currentPage}
              rowsPerPage={rowPerPage}
              customRowsPerPageOptions={[10, 20, 30, 50, 100]}
              setCurrentPage={(e) => setCurrentPage(e)}
              setRowsPerPage={(e) => setRowsPerPage(e)}
            />
          </div>
        </div>
      )}
    </div>
  )
}
